import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
  Paper,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  PlayCircleOutline as PlayIcon,
  Pause as PauseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export const PublicHeroSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Video URL from environment variable or Firebase Storage
  // If not set, will use default Firebase Storage URL pattern
  const heroVideoUrl = process.env.REACT_APP_HERO_VIDEO_URL || 
    'https://storage.googleapis.com/careva-bbec3.firebasestorage.app/hero-video/promo.mp4';

  const handleGetStarted = () => {
    navigate('/auth');
  };

  const handleWatchVideo = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
        color: 'white',
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />
      
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center">
          {/* Left Column - Content */}
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                  fontWeight: 'bold',
                  lineHeight: 1.2,
                  mb: 3,
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                Find Quality Care
                <br />
                <Box component="span" sx={{ color: theme.palette.secondary.light }}>
                  When You Need It
                </Box>
              </Typography>
              
              <Typography
                variant="h5"
                component="p"
                sx={{
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                  lineHeight: 1.6,
                  mb: 4,
                  opacity: 0.9,
                  maxWidth: { md: '500px' },
                }}
              >
                Connect with verified, professional caregivers for physical therapy, 
                occupational therapy, senior care, and home health services. 
                Book appointments, read reviews, and get the care you deserve.
              </Typography>

              {/* CTA Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  mb: 4,
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGetStarted}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    backgroundColor: 'white',
                    color: theme.palette.primary.main,
                    fontSize: '1.1rem',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                    '&:hover': {
                      backgroundColor: theme.palette.grey[100],
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Get Started Free
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleWatchVideo}
                  startIcon={<PlayIcon />}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    fontSize: '1.1rem',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Watch How It Works
                </Button>
              </Box>

              {/* Trust Indicators */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 3,
                  alignItems: 'center',
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  opacity: 0.9,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">✓</Typography>
                  <Typography variant="body2">Verified Caregivers</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">✓</Typography>
                  <Typography variant="body2">Background Checked</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">✓</Typography>
                  <Typography variant="body2">Insured & Bonded</Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Right Column - Visual */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: { xs: '300px', md: '500px' },
              }}
            >
              <Paper
                elevation={8}
                sx={{
                  width: '100%',
                  maxWidth: '500px',
                  height: '400px',
                  borderRadius: 3,
                  overflow: 'hidden',
                  position: 'relative',
                  backgroundColor: '#000',
                }}
              >
                {heroVideoUrl ? (
                  <>
                    <video
                      ref={videoRef}
                      src={heroVideoUrl}
                      autoPlay
                      loop
                      muted
                      playsInline
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                    />
                    {/* Play/Pause overlay button */}
                    <Box
                      onClick={handleWatchVideo}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        cursor: 'pointer',
                        opacity: isPlaying ? 0 : 0.8,
                        transition: 'opacity 0.3s, backgroundColor 0.3s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        '&:hover': { 
                          opacity: 1,
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        },
                      }}
                    >
                      {isPlaying ? (
                        <PauseIcon sx={{ fontSize: '3rem', color: 'white' }} />
                      ) : (
                        <PlayIcon sx={{ fontSize: '3rem', color: 'white' }} />
                      )}
                    </Box>
                  </>
                ) : (
                  // Fallback placeholder
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      textAlign: 'center',
                      p: 4,
                      height: '100%',
                      background: `linear-gradient(45deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
                    }}
                  >
                    <PlayIcon sx={{ fontSize: '4rem', mb: 2, opacity: 0.8 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      See Careva in Action
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Watch how patients and caregivers connect
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
